import type { FieldType } from "@/lib/crm-fields/validate";
import { validateFieldValue } from "@/lib/crm-fields/validate";

const TRUE_WORDS = new Set(["true", "1", "yes", "y"]);
const FALSE_WORDS = new Set(["false", "0", "no", "n"]);

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
// M/D/YYYY, MM/DD/YYYY, or the same with dashes — the common CSV/spreadsheet
// export shape. US month-first convention, matching this CRM's phone/locale
// assumptions elsewhere.
const SLASH_OR_DASH_DATE = /^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/;

// Round-trips year/month/day through Date.UTC and compares back: a plain
// 1-12 / 1-31 range check accepts calendar-impossible combinations like
// month=2, day=31 or day=29 on a non-leap year. Date.UTC rolls an
// out-of-range day into the next month, so a mismatch here means the
// input wasn't a real date (Codex P2, PR #5515). Known limitation:
// ECMA-262's legacy two-digit-year rule adds 1900 to a year in 0-99, so a
// zero-padded year like "0050" would wrongly fail this check — not worth
// guarding given this CRM's realistic contact-date range, and the failure
// mode is a safe rejection, not silent corruption.
function isRealCalendarDate(year: number, month: number, day: number): boolean {
  const date = new Date(Date.UTC(year, month - 1, day));
  return (
    date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day
  );
}

function coerceDate(raw: string): string {
  const trimmed = raw.trim();
  const isoMatch = trimmed.match(ISO_DATE);
  if (isoMatch) {
    // Already ISO-shaped, but shape alone doesn't prove it's a real date —
    // "2026-02-31" matches this regex just as well as "2026-02-28". Without
    // this check an already-ISO value bypassed the calendar validation
    // applied to the slash/dash path below entirely (Codex P2, PR #5515).
    const year = Number(isoMatch[1]);
    const month = Number(isoMatch[2]);
    const day = Number(isoMatch[3]);
    return isRealCalendarDate(year, month, day) ? trimmed : raw;
  }
  const m = trimmed.match(SLASH_OR_DASH_DATE);
  if (m) {
    const month = Number(m[1]);
    const day = Number(m[2]);
    const year = Number(m[3]);
    if (
      month >= 1 && month <= 12 && day >= 1 && day <= 31 &&
      isRealCalendarDate(year, month, day)
    ) {
      return `${m[3]}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    }
  }
  return raw;
}

/** Split one CSV cell using the exact delimiter contract the importer uses
 * for multiselect fields. Keeping this shared with review-option generation
 * prevents a cell such as "Red; Blue" from becoming one option while import
 * later writes two values. */
export function tokenizeImportMultiselectValue(raw: string): string[] {
  return raw
    .split(/[,;]/)
    .map((value) => value.trim())
    .filter((value) => value.length > 0);
}

/**
 * CSV cells are always plain strings, but validateFieldValue (contracts.ts's
 * setValue) requires an actual boolean for "boolean" and a string[] for
 * "multiselect" — a raw CSV string fails both unconditionally. Coerce a raw
 * CSV cell into the shape each field type expects before it reaches
 * setValue, so a well-formed cell doesn't always error. A value that still
 * doesn't parse is passed through unchanged so validateFieldValue's own
 * rejection — and the importer's existing per-row error handling — applies
 * exactly as it does for every other field type.
 */
export function coerceImportCustomFieldValue(
  type: FieldType,
  raw: string,
  options?: string[] | null,
): unknown {
  if (type === "boolean") {
    const norm = raw.trim().toLowerCase();
    if (TRUE_WORDS.has(norm)) return true;
    if (FALSE_WORDS.has(norm)) return false;
    return raw;
  }
  if (type === "multiselect") {
    const opts = options ?? [];
    return tokenizeImportMultiselectValue(raw)
      .map((s) => opts.find((o) => o.toLowerCase() === s.toLowerCase()) ?? s);
  }
  if (type === "date") {
    return coerceDate(raw);
  }
  return raw;
}

/**
 * Returns the first complete-column CSV value that would fail the exact
 * coercion + validation path used during import, or null when the selected
 * field contract can safely accept every non-blank value. Review must check
 * this before creating a definition: otherwise a later row can create or
 * update a contact before its custom-field write fails.
 */
export function firstInvalidImportCustomFieldValue(
  type: FieldType,
  rawValues: readonly string[],
  options?: string[] | null,
): string | null {
  for (const raw of rawValues) {
    const result = validateFieldValue(
      type,
      coerceImportCustomFieldValue(type, raw, options),
      options ?? undefined,
    );
    if (result.ok === false) return raw;
  }
  return null;
}
